import * as React from "react";

import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * Mirrors each header cell's text onto the matching body cells as
 * `data-label`. On phones the `responsive-stack` CSS turns every row into a
 * label/value card, so tables stay readable without per-page rewrites.
 */
function useStackedLabels(enabled: boolean) {
  const ref = React.useRef<HTMLTableElement>(null);

  React.useEffect(() => {
    const table = ref.current;
    if (!table || !enabled) return;

    const apply = () => {
      const heads = Array.from(table.querySelectorAll<HTMLElement>("thead th"));
      if (!heads.length) return;
      const labels = heads.map((h) => (h.textContent || "").trim());
      table.querySelectorAll<HTMLElement>("tbody tr").forEach((row) => {
        Array.from(row.children).forEach((cell, i) => {
          const label = labels[i];
          if (label && !(cell as HTMLElement).hasAttribute("colspan")) {
            (cell as HTMLElement).dataset.label = label;
          } else {
            delete (cell as HTMLElement).dataset.label;
          }
        });
      });
    };

    apply();
    const observer = new MutationObserver(() => apply());
    observer.observe(table, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [enabled]);

  return ref;
}

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, forwardedRef) => {
    const isMobile = useIsMobile();
    const innerRef = useStackedLabels(isMobile);
    React.useImperativeHandle(forwardedRef, () => innerRef.current as HTMLTableElement);

    return (
      <div className="relative w-full max-w-full overflow-x-auto">
        <table
          ref={innerRef}
          className={cn("w-full caption-bottom text-sm responsive-stack", className)}
          {...props}
        />
      </div>
    );
  },
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <thead ref={ref} className={cn("[&_tr]:border-b bg-muted/30 sticky top-0 z-10", className)} {...props} />,
);
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
  ),
);
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tfoot ref={ref} className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)} {...props} />
  ),
);
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn("border-b border-border/50 transition-colors data-[state=selected]:bg-muted hover:bg-muted/30", className)}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td ref={ref} className={cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", className)} {...props} />
  ),
);
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn("mt-4 text-sm text-muted-foreground", className)} {...props} />
  ),
);
TableCaption.displayName = "TableCaption";

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
